import EmptyState from '@aurora/shared-client/components/common/EmptyState/EmptyState';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import type {
  UserConstraints,
  UserSorts
} from '@aurora/shared-generated/types/graphql-schema-types';
import {
  LeaderboardTimeRange,
  SortDirection
} from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import { TextAlignment } from '@aurora/shared-types/texts/enums';
import {
  merge,
  UndefinedValueMergeBehavior
} from '@aurora/shared-utils/helpers/objects/ObjectHelper';
import React, { useCallback, useContext, useEffect } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { ItemType, TopContributorWidgetSort, UserViewVariant } from '../../../types/enums';
import type {
  UserViewFragment,
  UserViewsQuery,
  UserViewsQueryVariables
} from '../../../types/graphql-types';
import PaneledItemList from '../../common/List/PaneledItemList/PaneledItemList';
import EditableWidget from '../../common/Widget/EditableWidget';
import type { WidgetFC } from '../../common/Widget/types';
import type { ItemViewTypeAndProps } from '../../entities/types';
import useEntityViewQuery from '../../useEntityViewQuery';
import useTranslation from '../../useTranslation';
import type { UserListWidget } from '../types';
import type {
  UserViewCardProps,
  UserViewCommonProps,
  UserViewInlineProps
} from '../UserView/types';
import userViewsQuery from '../UserViews.query.graphql';
import localStyles from './UserListForNodeByTopContributorsWidget.module.pcss';

/**
 * Props for UserListForNodeByTopContributorsWidget
 */
export interface UserListForNodeByTopContributorsWidgetProps extends UserListWidget {
  /**
   * Specifies the sort order for the list.
   */
  sorts?: UserSorts;

  /**
   * Specifies the view of the user for the widget.
   */
  viewVariant?: ItemViewTypeAndProps<ItemType.USER, UserViewVariant>;
  /**
   * Specifies the time period selected.
   */
  timePeriod?: number;
  /**
   * Specifies the roles to exclude for users in the list.
   */
  rolesToExclude?: { id: string; name: string }[];
}

export const userViewCardInlineDefaults:
  | UserViewInlineProps
  | UserViewCardProps
  | UserViewCommonProps = {
  useAvatar: true,
  useRank: true,
  avatarSize: IconSize.PX_40,
  useFooterDivider: false,
  useKudosCount: true,
  useCount: true
};

export const userViewGridDefaults: UserViewInlineProps | UserViewCommonProps = {
  useAvatar: true,
  useLogin: false,
  useTitle: true,
  avatarSize: IconSize.PX_40,
  useKudosCount: true,
  useCount: true
};

/**
 * Map to store and get the sort by field values.
 */
export const sortsValueMap: Map<string, UserSorts> = new Map();
sortsValueMap.set('kudosReceivedCount', {
  kudosReceivedCount: {
    direction: SortDirection.Desc
  }
});
sortsValueMap.set('messagesCount', {
  messagesCount: {
    direction: SortDirection.Desc
  }
});
sortsValueMap.set('topicsCount', {
  topicsCount: {
    direction: SortDirection.Desc
  }
});
sortsValueMap.set('acceptedSolutionCount', {
  acceptedSolutionCount: {
    direction: SortDirection.Desc
  }
});

const date = new Date();
const endDate = new Date().toISOString();

/**
 * Returns true if the sort passed is same as the sort
 * we get from the sortsValueMap map by the passed keyToMatch
 * @param sort the current sort.
 * @param keyToMatch the key to search for in sortsValueMap.
 */
export function isSelectedSortMatchesWithGivenKey(sort: UserSorts, keyToMatch: string) {
  return JSON.stringify(sort) === JSON.stringify(sortsValueMap.get(keyToMatch));
}

const defaultProps: UserListForNodeByTopContributorsWidgetProps = {
  viewVariant: {
    type: UserViewVariant.INLINE,
    props: userViewCardInlineDefaults
  },
  useTitle: true,
  pageSize: 5,
  sorts: {
    kudosReceivedCount: {
      direction: SortDirection.Desc
    }
  },
  panelType: PanelType.STANDARD,
  useFooter: false,
  hideIfEmpty: true,
  timePeriod: -1,
  rolesToExclude: []
};

export function getFinalProps(
  props: UserListForNodeByTopContributorsWidgetProps
): UserListForNodeByTopContributorsWidgetProps {
  return merge(defaultProps, props, {
    undefinedMergeBehavior: UndefinedValueMergeBehavior.IGNORE_BEFORE_MERGE,
    mergeNested: false
  });
}

/**
 * A list of Top Contributing users for the current node.
 *
 * @constructor
 *
 * @author Shalin Amin
 */
const UserListForNodeByTopContributorsWidget: WidgetFC<
  UserListForNodeByTopContributorsWidgetProps
> = props => {
  const { isVisible, ...rest } = props;
  const finalProps: UserListForNodeByTopContributorsWidgetProps = getFinalProps(rest);
  const {
    listVariant,
    viewVariant,
    useTitle,
    pageSize,
    sorts,
    className,
    panelType,
    useFooter,
    hideIfEmpty,
    timePeriod,
    rolesToExclude
  } = finalProps;

  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.USER_LIST_FOR_NODE_BY_TOP_CONTRIBUTORS_WIDGET
  );
  const { contextNode } = useContext(AppContext);

  const useEmpty = !hideIfEmpty;

  const constraints: UserConstraints = {
    nodeId: {
      eq: contextNode.id
    },
    ...(rolesToExclude && {
      excludedRole: {
        in: rolesToExclude.map(fieldEntry => {
          return { roleName: fieldEntry.name };
        })
      }
    })
  };

  const timeToDaysMap: Record<number, LeaderboardTimeRange> = {
    1: LeaderboardTimeRange.OneDay,
    7: LeaderboardTimeRange.OneWeek,
    30: LeaderboardTimeRange.OneMonth,
    90: LeaderboardTimeRange.ThreeMonths,
    180: LeaderboardTimeRange.SixMonths,
    365: LeaderboardTimeRange.OneYear
  };

  /**
   * Builds the constraints on the basis of sort and time period.
   *
   *  @return The user constraints for the specified sort and time period.
   */
  function buildTimePeriodConstraints(): UserConstraints {
    let constraint;
    const copiedDate = new Date(date.getTime());
    copiedDate.setDate(copiedDate.getDate() - timePeriod);
    const startDate = copiedDate.toISOString();

    if (!timePeriod || timePeriod === -1) {
      if (
        isSelectedSortMatchesWithGivenKey(sorts, TopContributorWidgetSort.ACCEPTED_SOLUTIONS_COUNT)
      ) {
        return {
          acceptedSolutionsCount: {
            gt: 0
          }
        };
      }
      return null;
    }
    if (
      isSelectedSortMatchesWithGivenKey(sorts, TopContributorWidgetSort.ACCEPTED_SOLUTIONS_COUNT)
    ) {
      constraint = {
        acceptedSolutionPostTime: {
          gt: startDate,
          lt: endDate
        },
        acceptedSolutionsCount: {
          gt: 0
        }
      };
    } else if (isSelectedSortMatchesWithGivenKey(sorts, TopContributorWidgetSort.TOPICS_COUNT)) {
      constraint = {
        topicsPostTime: {
          gt: startDate,
          lt: endDate
        }
      };
    } else if (isSelectedSortMatchesWithGivenKey(sorts, TopContributorWidgetSort.MESSAGES_COUNT)) {
      constraint = {
        messagesPostTime: {
          gt: startDate,
          lt: endDate
        }
      };
    } else if (
      isSelectedSortMatchesWithGivenKey(sorts, TopContributorWidgetSort.KUDOS_RECEIVED_COUNT)
    ) {
      constraint = {
        kudosReceivedTimeRange: {
          eq: timeToDaysMap[timePeriod]
        }
      };
    }

    return constraint;
  }

  const timePeriodConstraints = buildTimePeriodConstraints();

  const queryResult = useEntityViewQuery<ItemType.USER, UserViewsQuery, UserViewsQueryVariables>(
    module,
    ItemType.USER,
    viewVariant,
    userViewsQuery,
    {
      variables: {
        constraints: {
          ...constraints,
          ...timePeriodConstraints
        },
        sorts,
        first: pageSize
      }
    },
    false
  );

  const { data: userData, loading } = queryResult;
  useEffect(() => {
    if (!useEmpty && !loading) {
      isVisible(userData?.users?.edges?.length !== 0);
    }
  }, [userData, isVisible, loading, useEmpty]);

  /**
   * A callback function to render Empty State when entity list has not users
   */
  const renderNoUsers = useCallback((): React.ReactElement => {
    return (
      <EmptyState description={formatMessage('emptyDescription')} alignment={TextAlignment.LEFT} />
    );
  }, [formatMessage]);

  if (textLoading) {
    return null;
  }

  function renderHeader(): React.ReactElement {
    return <h3 className={cx('h5 mb-0')}>{formatMessage('title')}</h3>;
  }

  return (
    <EditableWidget<UserListForNodeByTopContributorsWidgetProps> props={finalProps}>
      <PaneledItemList<
        UserViewFragment,
        ItemType.USER,
        ItemViewTypeAndProps<ItemType.USER, UserViewVariant>,
        UserViewsQuery,
        UserViewsQueryVariables
      >
        type={ItemType.USER}
        variant={viewVariant}
        queryResult={queryResult}
        header={useTitle && renderHeader}
        headerClassName={cx('lia-widget-header-title')}
        listVariant={listVariant}
        pageSize={pageSize}
        className={cx(className, 'lia-message-widget-panel')}
        empty={renderNoUsers}
        useHeaderWhenEmpty={useTitle}
        useEmpty={useEmpty}
        panel={panelType}
        useFooter={useFooter}
        bodyClassName={cx({ 'lia-message-widget-body': panelType === PanelType.DIVIDER })}
        id="UserListForNodeByTopContributorsWidget"
      />
    </EditableWidget>
  );
};
export default UserListForNodeByTopContributorsWidget;
